import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  FolderOpen, 
  ShoppingCart, 
  LogOut,
  Store,
  Globe,
  Truck,
  Gift,
  Images
} from 'lucide-react';

export const AdminLayout = ({ children }) => {
  const { isAuthenticated, logout, adminEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const navItems = [
    { href: '/admin/dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { href: '/admin/categories', label: 'Catégories', icon: FolderOpen },
    { href: '/admin/products', label: 'Produits', icon: Package },
    { href: '/admin/orders', label: 'Commandes', icon: ShoppingCart },
    { href: '/admin/shipping', label: 'Livraison', icon: Truck },
    { href: '/admin/gift-cards', label: 'Bons cadeaux', icon: Gift },
    { href: '/admin/media', label: 'Médiathèque', icon: Images },
    { href: '/admin/cms', label: 'Contenu du site', icon: Globe },
  ];

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 admin-sidebar flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <Link to="/" className="flex items-center gap-2">
            <Store className="w-6 h-6 text-primary" />
            <span className="font-heading text-lg font-semibold">Villa Marceau</span>
          </Link>
          <p className="text-xs text-muted-foreground mt-1">Administration</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`admin-nav-item ${isActive ? 'active' : ''}`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User info & logout */}
        <div className="p-4 border-t border-border">
          <p className="text-xs text-muted-foreground mb-3 truncate">{adminEmail}</p>
          <button
            onClick={handleLogout}
            className="admin-nav-item w-full text-destructive hover:bg-destructive/10"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
